import {
  getReadonlyListItemAsset,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type {
  IxcAssetsV1AssetState,
  IxcPortfolioV1AccountDelta,
  IxcPortfolioV1TaxLotAllocation,
  IxcPortfolioV1TransactionAsset,
  IxcPortfolioV1TransactionData,
  IxcPortfolioV1TransactionState,
} from '@interxfi/types/generated/api';
import { getAssetStateListItems } from '../../asset/asset-list-items';
import { getRaglTransactionDetailPath } from '../../clearing-ledger/transaction/ragl-transaction-routing';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getRegistrationAccountDetailPath } from '../../registration/account/registration-account-routing';
import { getTransactionDetailListItems } from '../../transaction-shared/transaction-list-items';
import { getProgramDetailPath } from '../../userauth/program/program-routing';
import { getPortfolioHoldReductionListItems, getPortfolioReverseLinkListItems } from '../shared-portfolio-list-items';
import { PortfolioTransactionStatusPill } from './portfolio-transaction-status-pill';

export function getPortfolioTaxLotAllocationListItems(data: IxcPortfolioV1TaxLotAllocation | undefined, prefix = '') {
  return {
    taxLotId: getReadonlyListItemId(`${prefix}taxLotId`, 'Tax Lot ID', data?.taxLotId),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioAccountDeltaListItems(
  data: IxcPortfolioV1AccountDelta | undefined,
  asset: IxcAssetsV1AssetState | undefined,
  prefix = '',
) {
  return {
    amount: getReadonlyListItemAsset(`${prefix}amount`, 'Amount', data?.amount, asset?.data),
    isReverse: getReadonlyListItemYesOrNo(`${prefix}isReverse`, 'Is Reverse', Boolean(data?.isReverse)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioTransactionAssetListItems(data: IxcPortfolioV1TransactionAsset | undefined, prefix = '') {
  return {
    asset: data?.asset ? getAssetStateListItems(data.asset) : undefined,
    isHomeCurrency: getReadonlyListItemYesOrNo(`${prefix}isHomeCurrency`, 'Is Home Currency', Boolean(data?.isHomeCurrency)),
    accountDeltas: data?.accountDeltas?.map((delta, index) =>
      getPortfolioAccountDeltaListItems(delta, data.asset, `${prefix}accountDeltas.${index}.`),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioTransactionDataListItems(
  data: IxcPortfolioV1TransactionData | undefined,
  accountId: string | undefined,
  hasRaglAccess: boolean,
  prefix = '',
) {
  return {
    settledAt: data?.settledAt ? getReadonlyListItemDate(`${prefix}settledAt`, 'Settled At', data.settledAt, 'timestamp_iso_ms') : undefined,
    settledByTransactionId: data?.settledByTransactionId
      ? getReadonlyListItemId(
          `${prefix}settledByTransactionId`,
          'Settled by Transaction ID',
          data.settledByTransactionId,
          hasRaglAccess ? getRaglTransactionDetailPath(data.settledByTransactionId) : undefined,
        )
      : undefined,
    assets: data?.assets?.map((asset, index) => getPortfolioTransactionAssetListItems(asset, `${prefix}assets.${index}.`)),
    effectiveAt: getReadonlyListItemDate(`${prefix}effectiveAt`, 'Effective At', data?.effectiveAt, 'timestamp_iso_ms'),
    settlesAt: getReadonlyListItemDate(`${prefix}settlesAt`, 'Settles At', data?.settlesAt, 'timestamp_iso_ms'),
    tradeDate: getReadonlyListItemDate(`${prefix}tradeDate`, 'Trade Date', data?.tradeDate, 'date_iso'),
    settleDate: getReadonlyListItemDate(`${prefix}settleDate`, 'Settle Date', data?.settleDate, 'date_iso'),
    detail: getTransactionDetailListItems(data?.detail, 'portfolio', `${prefix}detail.`),
    reverses: data?.reverses ? getPortfolioReverseLinkListItems(data.reverses, accountId, `${prefix}reverses.`) : undefined,
    reduceHold: data?.reduceHold ? getPortfolioHoldReductionListItems(data.reduceHold, accountId, `${prefix}reduceHold.`) : undefined,
    reversedBy: data?.reversedBy ? getPortfolioReverseLinkListItems(data.reversedBy, accountId, `${prefix}reversedBy.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioTransactionStateListItems(data: IxcPortfolioV1TransactionState | undefined, hasRaglAccess: boolean) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    accountId: getReadonlyListItemId('accountId', 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    programId: getReadonlyListItemId('programId', 'Program ID', data?.programId, getProgramDetailPath(data?.programId)),
    transactionId: getReadonlyListItemId('transactionId', 'Transaction ID', data?.transactionId),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcPortfolioV1',
      enumName: 'IxcPortfolioV1TransactionStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <PortfolioTransactionStatusPill status={data.status} /> : null),
    data: getPortfolioTransactionDataListItems(data?.data, data?.accountId, hasRaglAccess, 'data.'),
  } as const satisfies ReadonlyListItemRecord;
}
